import { runExpirySweep } from '../inventory/expirySweep';
import { expiredDisplaySignature } from './expiredProductsLifecycle';

const EMPTY_SWEEP_RESULT = {
  processedCount: 0,
  movementCount: 0,
  derivedExpired: []
};

async function readProductsLotsForBusiness(businessId) {
  const { readLotsFromBestSource } = await import('../../utils/lotsReadSource');
  const { readProductsFromBestSource } = await import('../../utils/productsReadSource');

  const [lotsList, productsList] = await Promise.all([
    readLotsFromBestSource(businessId),
    readProductsFromBestSource(businessId)
  ]);

  return {
    lotsList: Array.isArray(lotsList) ? lotsList : [],
    productsList: Array.isArray(productsList) ? productsList : []
  };
}

async function readInventoryRefreshForBusiness(businessId) {
  const { readLotsFromBestSource } = await import('../../utils/lotsReadSource');
  const { readProductsFromBestSource } = await import('../../utils/productsReadSource');
  const { readStockDataFromBestSource } = await import('../../utils/stockDataReadSource');

  const [lotsList, productsList, stockDataDoc] = await Promise.all([
    readLotsFromBestSource(businessId),
    readProductsFromBestSource(businessId),
    readStockDataFromBestSource(businessId)
  ]);

  return {
    lotsList: Array.isArray(lotsList) ? lotsList : [],
    productsList: Array.isArray(productsList) ? productsList : [],
    stockDataDoc: stockDataDoc && typeof stockDataDoc === 'object' ? stockDataDoc : null
  };
}

/**
 * Runs expiry sweep and returns state/cache patches (no React setState).
 * @returns {Promise<{
 *   result: object,
 *   patches: { expiredProducts?, products?, lots?, stockData? },
 *   cache: { alerts?, expired?, partial? },
 *   resetAutoRunFlag: boolean
 * }>}
 */
export async function orchestrateExpirySweep(businessId, { previousExpiredProducts = [] } = {}) {
  if (!businessId) {
    return {
      result: { ...EMPTY_SWEEP_RESULT },
      patches: {},
      cache: {},
      resetAutoRunFlag: false
    };
  }

  const result = await runExpirySweep(businessId);
  const patches = {};
  const cache = {};

  if (Array.isArray(result.derivedExpired)) {
    const nextSig = expiredDisplaySignature(result.derivedExpired);
    const prevSig = expiredDisplaySignature(previousExpiredProducts);
    if (nextSig !== prevSig) {
      patches.expiredProducts = result.derivedExpired;
    }

    const { lotsList, productsList } = await readProductsLotsForBusiness(businessId);
    cache.alerts = { products: productsList, lots: lotsList };
    cache.expired = {
      products: productsList,
      lots: lotsList,
      expiredProducts: result.derivedExpired
    };
  }

  if (result.processedCount > 0) {
    const { lotsList, productsList, stockDataDoc } = await readInventoryRefreshForBusiness(
      businessId
    );

    if (productsList.length > 0) {
      patches.products = productsList;
    }
    patches.lots = lotsList;
    if (stockDataDoc) {
      patches.stockData = stockDataDoc;
    }
    cache.partial = { products: productsList, lots: lotsList };

    return {
      result,
      patches,
      cache,
      resetAutoRunFlag: true
    };
  }

  return {
    result,
    patches,
    cache,
    resetAutoRunFlag: false
  };
}

export function applyExpirySweepCacheUpdates(cache = {}) {
  void import('../../services/cacheReadModel').then((crm) => {
    if (!crm.isReadModelReady?.()) return;

    const expiredPayload = cache.expired || {};
    const alertsPayload = cache.alerts || {};
    if (cache.alerts || cache.expired) {
      crm.syncExpiredProductsReadModel({
        expiredProducts: expiredPayload.expiredProducts,
        lots: expiredPayload.lots || alertsPayload.lots,
        products: expiredPayload.products || alertsPayload.products
      });
    }
    if (cache.partial) {
      // products/lots changed by sweep — silent entity sync + idle stock warm
      crm.syncEntities(cache.partial);
      crm.ensureWarm?.([crm.READ_MODELS.STOCK, crm.READ_MODELS.INVENTORY_METRICS], {
        priority: 'idle',
        data: cache.partial
      });
    }
  });
}

export function applyExpirySweepStatePatches(patches, setters) {
  if (!patches || !setters) return;

  if (patches.expiredProducts != null) {
    setters.setExpiredProducts(patches.expiredProducts);
  }
  if (patches.products != null) {
    setters.setProducts(patches.products);
  }
  if (patches.lots != null) {
    setters.setLots(patches.lots);
  }
  if (patches.stockData != null) {
    setters.setStockData(patches.stockData);
  }
}

export function createSkippedExpirySweepResult() {
  return {
    result: { ...EMPTY_SWEEP_RESULT, skipped: true },
    patches: {},
    cache: {},
    resetAutoRunFlag: false
  };
}

export { EMPTY_SWEEP_RESULT };
